import { ShiftProps } from "../interfacesProps/ShiftProps";
import React,{useContext} from "react";
import { Button, CircularProgress } from "@material-ui/core";
import { ShiftService } from "../api/ShiftService";
import {
  ShiftRelatedConstants,
  ShiftRelatedStylingConstants,
} from "../util/ShiftConstants";
import { makeStyles } from "@material-ui/core/styles";
import { roleNameEnum, NotAvailableString } from "../util/Common";
import { UIRelatedConstants } from "../theme/Theme";
import { ShiftListContext } from "./ShiftListContext";
import { RBButton } from "./RBButton";
import { RBContext } from './RBContext';
import { getRequestPaymentDisableInfo } from "../util/RequestPaymentReminder";
import { isPaymentInfoNotApplicable } from "../util/PaymentInfoAvailability";

interface RBShiftLayoutButtonProps {
  shift: ShiftProps;
  rolename: string;
}

const useStyles = makeStyles({
  root: {
    minWidth: 475,
    marginLeft: -250,
  },
  bullet: {
    display: "inline-block",
    margin: "0 2px",
    transform: "scale(0.8)",
  },
  CardContent: {
    paddingTop: 8,
    "&:last-child": {
      paddingBottom: 8,
    },
  },
  title: {
    fontSize: 14,
  },
  pos: {
    marginBottom: 12,
  },
 button: {
  position: "relative", // 👈 required so spinner stays inside button
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 120, // keeps button width stable
  height: 40, // 👈 optional: fixes height if it's shrinking
  borderRadius: UIRelatedConstants.ButtonBorderRadius,
  borderWidth: 1,
  backgroundColor: "#FFF",
  "&:hover": {
    backgroundColor: "#CDCDCD",
  },
},

spinner: {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)", // ✅ better centering
},
});

export const RBShiftLayoutButton: React.FC<RBShiftLayoutButtonProps> = ({
  ...props
}) => {
  const { shift, rolename } = props;
  //const shiftListContext = React.useContext(ShiftListContext);
  const { onAction,loadingShiftId } = React.useContext(ShiftListContext);

  const { industryMode,primaryColor,secondaryColor } = useContext(RBContext);
  //console.log("RBShiftLayoutButton ", onAction);

  const getCardAlignedColorForState = (stateValue: any) => {
    const stateGlobalColor = ShiftRelatedStylingConstants.Colors[stateValue];
    if (stateGlobalColor === "#2B3270") {
      return primaryColor;
    }
    if (stateGlobalColor === "#6EC9CD") {
      return secondaryColor;
    }
    return stateGlobalColor ?? primaryColor;
  };

  const getOutlinedButtonStyle = (borderColor: string, disabled?: boolean) => {
    const isDisabled = !!disabled;
    return {
      // Match mobile app disabled palette
      borderColor: isDisabled ? "rgb(223, 223, 223)" : borderColor,
      color: isDisabled ? "#808080" : borderColor,
      backgroundColor: isDisabled ? "#f2f2f2" : "#FFF",
      textTransform: "none" as const,
    };
  };

  const renderShiftLayoutButtonForWorker = () => {
      const isLoading = loadingShiftId === shift.id;
    console.log("props1",props)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const classes = useStyles();
    const shiftState = ShiftService.getStatusForShiftAsWorker(shift,rolename,industryMode);
    console.log("shiftState",shiftState)
    let buttonText = NotAvailableString;
    let borderColor, isButtonVisible;
    const actions: Array<{
      text: string;
      borderColor: string;
      disabled?: boolean;
      title?: string;
    }> = [];
    
    switch (shiftState) {
      case ShiftRelatedConstants.ShiftStateText.AwaitingHiring:
        actions.push({
          text: ShiftRelatedConstants.ShiftButtonText.WithdrawShift,
          borderColor: primaryColor,
        });
        isButtonVisible = false;

        break;
      case ShiftRelatedConstants.ShiftStateText.AwaitingWorkerConfirmation:
        actions.push({
          text: ShiftRelatedConstants.ShiftButtonText.ConfirmHours,
          borderColor: ShiftRelatedStylingConstants.Colors[shiftState],
        });
        isButtonVisible = false;

        break;
      case ShiftRelatedConstants.ShiftStateText.RateShift:
        actions.push({
          text: ShiftRelatedConstants.ShiftButtonText.RateShift,
          borderColor: primaryColor,
        });
        isButtonVisible = false;
        break; 
      case ShiftRelatedConstants.ShiftStateText.FeedbackShift:
         actions.push({
          text: ShiftRelatedConstants.ShiftButtonText.FeedbackShift,
          borderColor: primaryColor,
        });
        // Keep legacy behavior, but actual feedback action is added below as an independent condition
        isButtonVisible = false;
        break;

      case ShiftRelatedConstants.ShiftStateText.ShiftCompleted:
        // Payment-related actions are handled below as independent conditions.
        isButtonVisible = false;
        break;

          
      default:
        isButtonVisible = false;
      //console.log("shift status is default");
    }

    const isPaid = !!shift?.paymentData;
    const paymentInfoNotApplicable = isPaymentInfoNotApplicable(shift);
    const canShowPaymentActions =
      shift?.flags?.isCompletionAcknowledged === true &&
      shift?.flags?.isCancelled !== true &&
      !isPaid &&
      shift.networkPaid === false &&
      !paymentInfoNotApplicable;

    if (canShowPaymentActions) {
      const paymentActionColor = getCardAlignedColorForState(shiftState);
      actions.push({
        text: ShiftRelatedConstants.ShiftButtonText.MarkPaid,
        borderColor: paymentActionColor,
      });

      const requestPaymentDisableInfo = getRequestPaymentDisableInfo(shift);
      actions.push({
        text: ShiftRelatedConstants.ShiftButtonText.RequestPayment,
        borderColor: paymentActionColor,
        disabled: requestPaymentDisableInfo.disabled,
        title: requestPaymentDisableInfo.reason,
      });
    }

    // Additive conditions: allow multiple actions at once (e.g., Completed + Feedback needed)
    // NOTE: Order matters. We want payment actions first, then feedback.
//     const needsWorkerFeedback =
//       !shift.networkPaid &&
//       shift?.workerAutoAcknowledged === true &&
//       rolename !== roleNameEnum.owner &&
//       !shift?.workerFeedbackGiven;

//     if (needsWorkerFeedback) {
//       actions.push({
//         text: ShiftRelatedConstants.ShiftButtonText.FeedbackShift,
//         borderColor: primaryColor,
//       });
//     }

    // If we collected actions, render them; otherwise fall back to the legacy single-button path.
    if (actions.length > 0) {
      isButtonVisible = false;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {actions.length > 0
          ? (() => {
              const paymentActionOrder = [
                ShiftRelatedConstants.ShiftButtonText.MarkPaid,
                ShiftRelatedConstants.ShiftButtonText.RequestPayment,
              ];

              const orderedActions = [...actions].sort((a, b) => {
                const aIsPayment = paymentActionOrder.includes(a.text);
                const bIsPayment = paymentActionOrder.includes(b.text);
                if (aIsPayment && bIsPayment) {
                  return (
                    paymentActionOrder.indexOf(a.text) -
                    paymentActionOrder.indexOf(b.text)
                  );
                }
                if (aIsPayment) return -1;
                if (bIsPayment) return 1;
                return 0;
              });

              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  {orderedActions.map((action) => (
                    <span
                      key={action.text}
                      title={
                        action.text ===
                        ShiftRelatedConstants.ShiftButtonText.RequestPayment
                          ? undefined
                          : action.disabled
                            ? action.title
                            : undefined
                      }
                      style={{ display: "inline-flex" }}
                      onClick={(e) => {
                        if (
                          action.text ===
                            ShiftRelatedConstants.ShiftButtonText
                              .RequestPayment &&
                          action.disabled
                        ) {
                          e.preventDefault();
                          e.stopPropagation();
                          onAction(action.text, shift);
                        }
                      }}
                    >
                      <Button
                        className={classes.button}
                        variant="outlined"
                        style={getOutlinedButtonStyle(
                          action.borderColor,
                          action.disabled
                        )}
                        disabled={!!action.disabled}
                        onClick={() => onAction(action.text, shift)}
                      >
                        {action.text}
                      </Button>
                    </span>
                  ))}
                </div>
              );
            })()
          : null}

        {isButtonVisible && (
          <Button
            className={classes.button}
            variant="outlined"
            style={{
              borderColor: borderColor,
              color: borderColor,
              textTransform: "none",
            }}
            onClick={() => onAction(buttonText, shift)}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" className={classes.spinner} />
            ) : (
              buttonText 
            )}
          </Button>
        )}
      </div>
    );
  };

  const renderShiftLayoutButtonForPharmacy = () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const classes = useStyles();
    const shiftState = ShiftService.getStatusForShiftAsPharmacy(shift,rolename,industryMode);
    let buttonText = NotAvailableString;
    let borderColor, isButtonVisible;
    const isLoading = loadingShiftId === shift.id;
    const actions: Array<{ text: string; borderColor: string }> = [];
    switch (shiftState) {
      case ShiftRelatedConstants.ShiftStateText.AwaitingHiring:
        if (shift.flags.isEditable) {
          buttonText = ShiftRelatedConstants.ShiftButtonText.EditShift;
          borderColor = primaryColor;
          isButtonVisible = true;
        } else {
          buttonText = ShiftRelatedConstants.ShiftButtonText.ViewApplications;
          borderColor = primaryColor;
          isButtonVisible = true;
        }

        break;
      case ShiftRelatedConstants.ShiftStateText.AwaitingHoursConfirmation:
        buttonText = ShiftRelatedConstants.ShiftButtonText.ConfirmHours;
        borderColor = ShiftRelatedStylingConstants.Colors[shiftState];
        isButtonVisible = true;

        break;
      case ShiftRelatedConstants.ShiftStateText.RateShift:
        buttonText = ShiftRelatedConstants.ShiftButtonText.RateShift;
        borderColor = ShiftRelatedStylingConstants.Colors[shiftState];
        isButtonVisible = true;

        break;
      case ShiftRelatedConstants.ShiftStateText.FeedbackShift:
        buttonText = ShiftRelatedConstants.ShiftButtonText.FeedbackShift;
        borderColor = primaryColor;
        isButtonVisible = true;
        break;

      default:
        isButtonVisible = false;
    }

    // Additive conditions: allow payment-related actions to appear alongside other state-driven UI.
    const isPaid = !!shift?.paymentData;
    const paymentInfoNotApplicable = isPaymentInfoNotApplicable(shift);
    const canShowPaymentActions =
      shift?.flags?.isCompletionAcknowledged === true &&
      shift?.flags?.isCancelled !== true &&
      !isPaid &&
      shift.networkPaid === false &&
      !paymentInfoNotApplicable;

    if (canShowPaymentActions) {
       const paymentActionColor = getCardAlignedColorForState(shiftState);
      actions.push({
        text: ShiftRelatedConstants.ShiftButtonText.MarkPaid,
        borderColor: paymentActionColor,
      });
    }

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        {actions.map((action) => (
          <Button
            key={action.text}
            className={classes.button}
            variant="outlined"
            style={getOutlinedButtonStyle(action.borderColor, false)}
            onClick={() => onAction(action.text, shift)}
          >
            {action.text}
          </Button>
        ))}

        {isButtonVisible && (
          <Button
            className={classes.button}
            variant="outlined"
            style={getOutlinedButtonStyle(borderColor, false)}
            onClick={() => onAction(buttonText, shift)}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" className={classes.spinner} />
            ) : (
              buttonText 
            )}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div>
      {rolename === roleNameEnum.pharmacist &&
        renderShiftLayoutButtonForWorker()}

      {rolename === roleNameEnum.owner && renderShiftLayoutButtonForPharmacy()}
    </div>
  );
};
